import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import {
  addTicketMessage,
  createTicket,
  escalateTicket,
  getTicket,
  listTickets,
  updateTicket,
} from "../api/endpoints/tickets"
import { listUsers } from "../api/endpoints/users"
import {
  Button,
  DataTable,
  Drawer,
  FormField,
  Input,
  Modal,
  PageSection,
  Select,
  StatusPill,
  TableBody,
  TableHead,
  TableRow,
  Td,
  Textarea,
  Th,
} from "../components/ui"
import { useAuth } from "../stores/auth"
import { formatDateTime, formatEnumLabel } from "../utils/formatting"

const TICKET_TYPES = [
  "CLIENT_DISPUTE",
  "PAYMENT_ISSUE",
  "ACCOUNT_ISSUE",
  "TECHNICAL_ISSUE",
  "ESCALATION",
  "OTHER",
]
const PRIORITIES = ["LOW", "MEDIUM", "HIGH", "URGENT"]
const STATUSES = ["OPEN", "IN_PROGRESS", "AWAITING_INFO", "RESOLVED", "CLOSED"]

function allowedAssigneeRoles(role: string) {
  return role === "ADMIN"
    ? ["AGENT", "VERIFICATION_STAFF"]
    : ["SUPER_ADMIN", "ADMIN", "OPS_MANAGER", "AGENT", "VERIFICATION_STAFF"]
}

function priorityTone(p: string): "neutral" | "info" | "warning" | "danger" {
  switch (p) {
    case "URGENT":
      return "danger"
    case "HIGH":
      return "warning"
    case "MEDIUM":
      return "info"
    default:
      return "neutral"
  }
}

function statusTone(s: string): "neutral" | "info" | "warning" | "success" | "danger" {
  switch (s) {
    case "OPEN":
      return "info"
    case "IN_PROGRESS":
      return "info"
    case "AWAITING_INFO":
      return "warning"
    case "RESOLVED":
      return "success"
    case "CLOSED":
      return "neutral"
    default:
      return "neutral"
  }
}

// ─── Main Page ───────────────────────────────────────────────

export function TicketsPage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const role = user?.role ?? ""
  const isManagement = ["SUPER_ADMIN", "ADMIN", "OPS_MANAGER"].includes(role)

  const [search, setSearch] = useState(() => searchParams.get("search") ?? "")
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get("status") ?? "")
  const [priorityFilter, setPriorityFilter] = useState(() => searchParams.get("priority") ?? "")
  const [typeFilter, setTypeFilter] = useState(() => searchParams.get("ticket_type") ?? "")
  const [page, setPage] = useState(() => Number(searchParams.get("page") ?? "1"))
  const [showCreate, setShowCreate] = useState(false)
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(
    () => searchParams.get("selected_ticket_id") ?? null,
  )

  useEffect(() => {
    const routeSearch = searchParams.get("search") ?? ""
    if (routeSearch !== search) {
      setSearch(routeSearch)
    }

    const routeStatus = searchParams.get("status") ?? ""
    if (routeStatus !== statusFilter) {
      setStatusFilter(routeStatus)
    }

    const routePriority = searchParams.get("priority") ?? ""
    if (routePriority !== priorityFilter) {
      setPriorityFilter(routePriority)
    }

    const routeType = searchParams.get("ticket_type") ?? ""
    if (routeType !== typeFilter) {
      setTypeFilter(routeType)
    }

    const routePage = Number(searchParams.get("page") ?? "1")
    if (routePage !== page) {
      setPage(routePage)
    }

    const routeSelectedTicketId = searchParams.get("selected_ticket_id") ?? null
    if (routeSelectedTicketId !== selectedTicketId) {
      setSelectedTicketId(routeSelectedTicketId)
    }
  }, [page, priorityFilter, search, searchParams, selectedTicketId, statusFilter, typeFilter])

  function updateRouteState(next: {
    search?: string | null
    status?: string | null
    priority?: string | null
    ticketType?: string | null
    page?: number | null
    selectedTicketId?: string | null
  }) {
    const params = new URLSearchParams(searchParams)

    if (next.search !== undefined) {
      if (next.search) {
        params.set("search", next.search)
      } else {
        params.delete("search")
      }
    }

    if (next.status !== undefined) {
      if (next.status) {
        params.set("status", next.status)
      } else {
        params.delete("status")
      }
    }

    if (next.priority !== undefined) {
      if (next.priority) {
        params.set("priority", next.priority)
      } else {
        params.delete("priority")
      }
    }

    if (next.ticketType !== undefined) {
      if (next.ticketType) {
        params.set("ticket_type", next.ticketType)
      } else {
        params.delete("ticket_type")
      }
    }

    if (next.page !== undefined) {
      if (next.page && next.page > 1) {
        params.set("page", String(next.page))
      } else {
        params.delete("page")
      }
    }

    if (next.selectedTicketId !== undefined) {
      if (next.selectedTicketId) {
        params.set("selected_ticket_id", next.selectedTicketId)
      } else {
        params.delete("selected_ticket_id")
      }
    }

    setSearchParams(params)
  }

  function openTicket(ticketId: string) {
    setSelectedTicketId(ticketId)
    updateRouteState({ selectedTicketId: ticketId })
  }

  const listQuery = useQuery({
    queryKey: ["tickets", search, statusFilter, priorityFilter, typeFilter, page],
    queryFn: () =>
      listTickets({
        ...(search ? { search } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(priorityFilter ? { priority: priorityFilter } : {}),
        ...(typeFilter ? { ticket_type: typeFilter } : {}),
        page: String(page),
        per_page: "25",
      }),
  })

  const rows = listQuery.data?.data ?? []
  const meta = listQuery.data?.meta

  // KPI strip counts
  const openCount = rows.filter(
    (r: any) => r.status === "OPEN" || r.status === "IN_PROGRESS",
  ).length
  const urgentCount = rows.filter((r: any) => r.priority === "URGENT").length
  const breachedCount = rows.filter((r: any) => r.slaBreached).length

  return (
    <div className="flex flex-col gap-5">
      {/* KPI Strip */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
        <KpiCard label="Open / In Progress" value={openCount} />
        <KpiCard label="Urgent" value={urgentCount} color="text-red-600" />
        <KpiCard label="SLA Breached" value={breachedCount} color="text-amber-600" />
        <KpiCard label="Total (page)" value={rows.length} />
      </div>

      <PageSection
        title={isManagement ? "Tickets" : "My Tickets"}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <input
              className="w-56 px-3 py-1.5 border border-slate-200 rounded-md text-sm bg-slate-50 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
              onChange={(e) => {
                const nextValue = e.target.value
                setSearch(nextValue)
                setPage(1)
                updateRouteState({ search: nextValue || null, page: null })
              }}
              placeholder="Search tickets..."
              value={search}
            />
            <select
              className="px-3 py-1.5 border border-slate-200 rounded-md text-sm bg-white"
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setPage(1)
                updateRouteState({ status: e.target.value || null, page: null })
              }}
              value={statusFilter}
            >
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {formatEnumLabel(s)}
                </option>
              ))}
            </select>
            <select
              className="px-3 py-1.5 border border-slate-200 rounded-md text-sm bg-white"
              onChange={(e) => {
                setPriorityFilter(e.target.value)
                setPage(1)
                updateRouteState({ priority: e.target.value || null, page: null })
              }}
              value={priorityFilter}
            >
              <option value="">All priorities</option>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {formatEnumLabel(p)}
                </option>
              ))}
            </select>
            <select
              className="px-3 py-1.5 border border-slate-200 rounded-md text-sm bg-white"
              onChange={(e) => {
                setTypeFilter(e.target.value)
                setPage(1)
                updateRouteState({ ticketType: e.target.value || null, page: null })
              }}
              value={typeFilter}
            >
              <option value="">All types</option>
              {TICKET_TYPES.map((t) => (
                <option key={t} value={t}>
                  {formatEnumLabel(t)}
                </option>
              ))}
            </select>
            <Button size="sm" onClick={() => setShowCreate(true)}>
              {isManagement ? "Create Ticket" : "Raise Ticket"}
            </Button>
          </div>
        }
      >
        <DataTable>
          <TableHead>
            <Th>ID</Th>
            <Th>Subject</Th>
            <Th>Type</Th>
            <Th>Priority</Th>
            <Th>Status</Th>
            <Th>SLA</Th>
            <Th>Raised By</Th>
            <Th>Assigned To</Th>
            <Th>Created</Th>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <tr>
                <td className="px-4 py-8 text-center text-slate-500" colSpan={9}>
                  No tickets found.
                </td>
              </tr>
            ) : (
              rows.map((row: any) => (
                <TableRow key={row.ticketId} onClick={() => openTicket(row.ticketId)}>
                  <Td className="font-mono font-bold text-blue-600">{row.ticketId}</Td>
                  <Td className="max-w-[200px] truncate">{row.subject}</Td>
                  <Td>
                    <StatusPill tone="neutral">{formatEnumLabel(row.ticketType)}</StatusPill>
                  </Td>
                  <Td>
                    <StatusPill tone={priorityTone(row.priority)}>{row.priority}</StatusPill>
                  </Td>
                  <Td>
                    <StatusPill tone={statusTone(row.status)}>
                      {formatEnumLabel(row.status)}
                    </StatusPill>
                  </Td>
                  <Td>
                    {row.slaBreached ? (
                      <span className="text-red-600 font-bold text-xs">BREACHED</span>
                    ) : row.slaDeadline ? (
                      <span className="text-xs text-slate-500">
                        {formatDateTime(row.slaDeadline)}
                      </span>
                    ) : (
                      "-"
                    )}
                  </Td>
                  <Td>{row.raisedByName ?? "-"}</Td>
                  <Td>{row.assignedToName ?? "-"}</Td>
                  <Td>{formatDateTime(row.createdAt)}</Td>
                </TableRow>
              ))
            )}
          </TableBody>
        </DataTable>

        {meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
            <p className="text-xs text-slate-500">
              Page {meta.page} of {meta.totalPages} ({meta.total} total)
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => {
                  const nextPage = page - 1
                  setPage(nextPage)
                  updateRouteState({ page: nextPage })
                }}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= meta.totalPages}
                onClick={() => {
                  const nextPage = page + 1
                  setPage(nextPage)
                  updateRouteState({ page: nextPage })
                }}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </PageSection>

      {/* Create Ticket Modal */}
      <CreateTicketModal
        open={showCreate}
        role={role}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          setShowCreate(false)
          queryClient.invalidateQueries({ queryKey: ["tickets"] })
        }}
      />

      {/* Ticket Detail Drawer */}
      <TicketDetailDrawer
        ticketId={selectedTicketId}
        role={role}
        isManagement={isManagement}
        onClose={() => {
          setSelectedTicketId(null)
          updateRouteState({ selectedTicketId: null })
        }}
      />
    </div>
  )
}

// ─── KPI Card ────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  color = "text-slate-800",
}: { label: string; value: number; color?: string }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <p className="text-xs text-slate-500 font-medium">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
    </div>
  )
}

// ─── Create Ticket Modal ─────────────────────────────────────

function CreateTicketModal({
  open,
  role,
  onClose,
  onCreated,
}: {
  open: boolean
  role: string
  onClose: () => void
  onCreated: () => void
}) {
  const [form, setForm] = useState({
    ticket_type: "CLIENT_DISPUTE",
    priority: "MEDIUM",
    subject: "",
    description: "",
    client_id: "",
    assigned_to: "",
  })
  const [error, setError] = useState("")
  const assignableRoles = allowedAssigneeRoles(role)
  const assignableUsersQuery = useQuery({
    queryKey: ["ticket-assignable-users", role],
    queryFn: () => listUsers({ per_page: "100" }),
    enabled: open && ["SUPER_ADMIN", "ADMIN"].includes(role),
  })
  const assignableUsers = (assignableUsersQuery.data?.data ?? []).filter((entry: any) =>
    assignableRoles.includes(entry.role),
  )

  const mutation = useMutation({
    mutationFn: () =>
      createTicket({
        ticket_type: form.ticket_type,
        priority: form.priority,
        subject: form.subject,
        description: form.description,
        ...(form.client_id ? { client_id: form.client_id } : {}),
        ...(form.assigned_to ? { assigned_to: form.assigned_to } : {}),
      }),
    onSuccess: () => {
      setForm({
        ticket_type: "CLIENT_DISPUTE",
        priority: "MEDIUM",
        subject: "",
        description: "",
        client_id: "",
        assigned_to: "",
      })
      setError("")
      onCreated()
    },
    onError: (err: any) => setError(err.message ?? "Failed to create ticket"),
  })

  const update = (key: string, value: string) => setForm((f) => ({ ...f, [key]: value }))
  const isManagement = ["SUPER_ADMIN", "ADMIN", "OPS_MANAGER"].includes(role)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isManagement ? "Create Ticket" : "Raise Ticket"}
      description={isManagement ? "Create a new support ticket." : "Raise a new support ticket."}
    >
      <div className="flex flex-col gap-4">
        <FormField label="Type">
          <Select value={form.ticket_type} onChange={(e) => update("ticket_type", e.target.value)}>
            {TICKET_TYPES.map((t) => (
              <option key={t} value={t}>
                {formatEnumLabel(t)}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Priority">
          <Select value={form.priority} onChange={(e) => update("priority", e.target.value)}>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {formatEnumLabel(p)}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Subject">
          <Input
            value={form.subject}
            onChange={(e) => update("subject", e.target.value)}
            placeholder="Brief summary"
          />
        </FormField>
        <FormField label="Description">
          <Textarea
            rows={4}
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            placeholder="Detailed description..."
          />
        </FormField>
        <FormField label="Client ID (optional)">
          <Input
            value={form.client_id}
            onChange={(e) => update("client_id", e.target.value)}
            placeholder="CLT-00001"
          />
        </FormField>
        {["SUPER_ADMIN", "ADMIN"].includes(role) ? (
          <FormField label="Assign To (optional)">
            <Select
              value={form.assigned_to}
              onChange={(e) => update("assigned_to", e.target.value)}
            >
              <option value="">Leave unassigned</option>
              {assignableUsers.map((entry: any) => (
                <option key={entry.userId} value={entry.userId}>
                  {entry.fullName} · {formatEnumLabel(entry.role)}
                </option>
              ))}
            </Select>
          </FormField>
        ) : null}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Creating..." : "Create"}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

// ─── Ticket Detail Drawer ────────────────────────────────────

function TicketDetailDrawer({
  ticketId,
  role,
  isManagement,
  onClose,
}: {
  ticketId: string | null
  role: string
  isManagement: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()

  const detailQuery = useQuery({
    queryKey: ["ticket-detail", ticketId],
    queryFn: () => getTicket(ticketId!),
    enabled: Boolean(ticketId),
  })

  const ticket = detailQuery.data?.data

  if (!ticketId) return null

  return (
    <Drawer
      title={ticketId ? `Ticket ${ticketId}` : "Ticket"}
      open={Boolean(ticketId)}
      onClose={onClose}
    >
      {detailQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading...</p>
      ) : !ticket ? (
        <p className="text-sm text-slate-500">Ticket not found.</p>
      ) : (
        <div className="flex flex-col gap-6">
          {/* Header badges */}
          <div className="flex flex-wrap gap-2">
            <StatusPill tone="neutral">{formatEnumLabel(ticket.ticketType)}</StatusPill>
            <StatusPill tone={priorityTone(ticket.priority)}>{ticket.priority}</StatusPill>
            <StatusPill tone={statusTone(ticket.status)}>
              {formatEnumLabel(ticket.status)}
            </StatusPill>
            {ticket.slaBreached && <StatusPill tone="danger">SLA BREACHED</StatusPill>}
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <MetaItem label="Subject" value={ticket.subject} />
            <MetaItem label="Raised By" value={ticket.raisedByName ?? "-"} />
            <MetaItem label="Assigned To" value={ticket.assignedToName ?? "-"} />
            {ticket.clientName && (
              <MetaItem label="Client" value={`${ticket.clientId} - ${ticket.clientName}`} />
            )}
            {ticket.loanId && <MetaItem label="Loan" value={ticket.loanId} />}
            <MetaItem
              label="SLA Deadline"
              value={ticket.slaDeadline ? formatDateTime(ticket.slaDeadline) : "-"}
            />
            <MetaItem label="Created" value={formatDateTime(ticket.createdAt)} />
            <MetaItem label="Updated" value={formatDateTime(ticket.updatedAt)} />
            {ticket.resolvedAt && (
              <MetaItem label="Resolved" value={formatDateTime(ticket.resolvedAt)} />
            )}
            {ticket.closedAt && <MetaItem label="Closed" value={formatDateTime(ticket.closedAt)} />}
          </div>

          {/* Description */}
          <div>
            <p className="text-xs font-medium text-slate-500 mb-1">Description</p>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{ticket.description}</p>
          </div>

          {/* Management Controls */}
          {isManagement && ticket.status !== "CLOSED" && (
            <TicketControls
              ticketId={ticketId}
              ticket={ticket}
              role={role}
              onUpdated={() => {
                queryClient.invalidateQueries({ queryKey: ["ticket-detail", ticketId] })
                queryClient.invalidateQueries({ queryKey: ["tickets"] })
              }}
            />
          )}

          {/* Messages */}
          <div>
            <p className="text-sm font-bold text-slate-800 mb-3">
              Messages ({ticket.messages?.length ?? 0})
            </p>
            <div className="flex flex-col gap-3">
              {(ticket.messages ?? []).map((msg: any) => (
                <div key={msg.messageId} className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-slate-700">{msg.authorName}</span>
                    <span className="text-xs text-slate-400">{msg.authorRole}</span>
                    <span className="text-xs text-slate-400 ml-auto">
                      {formatDateTime(msg.createdAt)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{msg.body}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Add Message */}
          {ticket.status !== "CLOSED" && (
            <AddMessageForm
              ticketId={ticketId}
              onSent={() => {
                queryClient.invalidateQueries({ queryKey: ["ticket-detail", ticketId] })
                queryClient.invalidateQueries({ queryKey: ["tickets"] })
              }}
            />
          )}
        </div>
      )}
    </Drawer>
  )
}

// ─── Meta Item ───────────────────────────────────────────────

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-sm font-medium text-slate-800">{value}</p>
    </div>
  )
}

// ─── Ticket Controls ─────────────────────────────────────────

function TicketControls({
  ticketId,
  ticket,
  role,
  onUpdated,
}: {
  ticketId: string
  ticket: any
  role: string
  onUpdated: () => void
}) {
  const isAdmin = role === "SUPER_ADMIN" || role === "ADMIN"
  const isOps = role === "OPS_MANAGER"
  const assignableRoles = allowedAssigneeRoles(role)
  const assignableUsersQuery = useQuery({
    queryKey: ["ticket-assignable-users", role, ticketId],
    queryFn: () => listUsers({ per_page: "100" }),
    enabled: isAdmin,
  })
  const assignableUsers = (assignableUsersQuery.data?.data ?? []).filter((entry: any) =>
    assignableRoles.includes(entry.role),
  )

  const updateMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => updateTicket(ticketId, body),
    onSuccess: onUpdated,
  })

  const escalateMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => escalateTicket(ticketId, body),
    onSuccess: onUpdated,
  })

  const closeMutation = useMutation({
    mutationFn: async () => {
      if (closeSummary.trim().length < 5) {
        throw new Error("Add a short resolution summary before closing the ticket.")
      }

      await addTicketMessage(ticketId, { body: closeSummary.trim() })
      await updateTicket(ticketId, { status: "CLOSED" })
    },
    onSuccess: () => {
      setShowClose(false)
      setCloseSummary("")
      onUpdated()
    },
  })

  const [escalateReason, setEscalateReason] = useState("")
  const [showEscalate, setShowEscalate] = useState(false)
  const [escalateTo, setEscalateTo] = useState("")
  const [assignedTo, setAssignedTo] = useState(ticket.assignedToId ?? "")
  const [showClose, setShowClose] = useState(false)
  const [closeSummary, setCloseSummary] = useState("")

  useEffect(() => {
    setAssignedTo(ticket.assignedToId ?? "")
  }, [ticket.assignedToId])

  const handleStatusChange = useCallback(
    (status: string) => {
      updateMutation.mutate({ status })
    },
    [updateMutation],
  )

  const handleEscalate = useCallback(() => {
    if (escalateReason.length < 3) return
    escalateMutation.mutate({
      reason: escalateReason,
      ...(escalateTo ? { escalateTo } : {}),
    })
    setShowEscalate(false)
    setEscalateReason("")
    setEscalateTo("")
  }, [escalateMutation, escalateReason, escalateTo])

  // Available status transitions
  const availableStatuses = STATUSES.filter((s) => {
    if (s === ticket.status) return false
    if (s === "CLOSED") return false
    if (isOps) return s === "IN_PROGRESS" || s === "AWAITING_INFO"
    return true
  })

  return (
    <div className="bg-slate-50 rounded-lg p-4 flex flex-col gap-3">
      <p className="text-xs font-bold text-slate-500 uppercase">Actions</p>
      <div className="flex flex-wrap gap-2">
        {availableStatuses.map((s) => (
          <Button
            key={s}
            variant={s === "CLOSED" ? "danger" : "secondary"}
            size="sm"
            onClick={() => handleStatusChange(s)}
            disabled={updateMutation.isPending}
          >
            {formatEnumLabel(s)}
          </Button>
        ))}

        {(isAdmin || isOps) && ticket.priority !== "URGENT" && (
          <Button variant="danger" size="sm" onClick={() => setShowEscalate(!showEscalate)}>
            Escalate
          </Button>
        )}

        {isAdmin ? (
          <Button variant="danger" size="sm" onClick={() => setShowClose((current) => !current)}>
            Close Ticket
          </Button>
        ) : null}
      </div>

      {isAdmin && (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Priority:</span>
            <Select
              className="!w-auto"
              value={ticket.priority}
              onChange={(e) => updateMutation.mutate({ priority: e.target.value })}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-64">
              <p className="text-xs text-slate-500 mb-1">Reroute To</p>
              <Select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
                <option value="">Leave unassigned</option>
                {assignableUsers.map((entry: any) => (
                  <option key={entry.userId} value={entry.userId}>
                    {entry.fullName} · {formatEnumLabel(entry.role)}
                  </option>
                ))}
              </Select>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => updateMutation.mutate({ assignedTo: assignedTo || null })}
              disabled={updateMutation.isPending}
            >
              Save Routing
            </Button>
          </div>
        </div>
      )}

      {showEscalate && (
        <div className="flex flex-col gap-2 mt-2">
          <Textarea
            rows={2}
            placeholder="Reason for escalation..."
            value={escalateReason}
            onChange={(e) => setEscalateReason(e.target.value)}
          />
          {isAdmin ? (
            <Select value={escalateTo} onChange={(e) => setEscalateTo(e.target.value)}>
              <option value="">Keep current assignee</option>
              {assignableUsers.map((entry: any) => (
                <option key={entry.userId} value={entry.userId}>
                  {entry.fullName} · {formatEnumLabel(entry.role)}
                </option>
              ))}
            </Select>
          ) : null}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="danger"
              onClick={handleEscalate}
              disabled={escalateMutation.isPending}
            >
              Confirm Escalation
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setShowEscalate(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {showClose && isAdmin ? (
        <div className="flex flex-col gap-2 mt-2">
          <Textarea
            rows={3}
            placeholder="Resolution summary to append before closing..."
            value={closeSummary}
            onChange={(e) => setCloseSummary(e.target.value)}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="danger"
              onClick={() => closeMutation.mutate()}
              disabled={closeMutation.isPending}
            >
              Confirm Close
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setShowClose(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {(updateMutation.isError || escalateMutation.isError || closeMutation.isError) && (
        <p className="text-xs text-red-600">
          {(updateMutation.error as any)?.message ??
            (escalateMutation.error as any)?.message ??
            (closeMutation.error as any)?.message ??
            "Action failed"}
        </p>
      )}
    </div>
  )
}

// ─── Add Message Form ────────────────────────────────────────

function AddMessageForm({ ticketId, onSent }: { ticketId: string; onSent: () => void }) {
  const [body, setBody] = useState("")

  const mutation = useMutation({
    mutationFn: () => addTicketMessage(ticketId, { body }),
    onSuccess: () => {
      setBody("")
      onSent()
    },
  })

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-bold text-slate-500 uppercase">Add Message</p>
      <Textarea
        rows={3}
        placeholder="Type a message..."
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || body.trim().length === 0}
        >
          {mutation.isPending ? "Sending..." : "Send"}
        </Button>
      </div>
      {mutation.isError && (
        <p className="text-xs text-red-600">
          {(mutation.error as any)?.message ?? "Failed to send message"}
        </p>
      )}
    </div>
  )
}
